import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import { index } from '@last/core'

import { floorplansApi } from '@/api/client'
import { useTabsStore } from '@/store/tabs'
import type { Floorplan, TableWithTabs } from '@/types'

export const useFloorplanStore = defineStore(
  'tables',
  () => {
    const tabsStore = useTabsStore()

    const floorplans = shallowRef<Record<string, Floorplan>>({})
    const selectedFloorplan = ref<string | undefined>()
    const floorplanMode = ref<'time' | 'diners' | 'amount' | 'reservation'>('time')

    const tables = computed(() => {
      return index(
        Object.values(floorplans.value).flatMap(f => f.tables),
        'id'
      )
    })

    const floorplanIds = computed(() => Object.keys(floorplans.value))

    const floorplanList = computed(() => {
      return Object.values(floorplans.value)
        .sort((a, b) => a.position - b.position)
        .map(floorplan => {
          return {
            ...floorplan,
            tables: floorplan.tables.map(table => {
              const tabs = Object.values(tabsStore.tabs).filter(
                tab => tab.open && tab.tables.includes(table.id)
              )
              return {
                ...table,
                tabIds: tabs.map(tab => tab.id)
              }
            })
          }
        })
    })

    const allTables = computed<TableWithTabs[]>(() => {
      return floorplanList.value.flatMap(floorplan => floorplan.tables)
    })

    function getTableFloorplan(tableId: string) {
      const table = tables.value[tableId]
      return floorplans.value[table?.floorplanId]
    }

    async function refreshFloorplans() {
      floorplans.value = await floorplansApi.getFloorplans()
    }

    return {
      floorplanIds,
      floorplanMode,
      floorplans,
      floorplanList,
      selectedFloorplan,
      tables,
      allTables,
      getTableFloorplan,
      refreshFloorplans
    }
  },
  {
    persist: true
  }
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useFloorplanStore, import.meta.hot))
}
